"use client";

import { useInView } from "framer-motion";
import { useEffect, useRef } from "react";
const dict = {
  ".": "period",
  ",": "comma",
};
const getKeyId = (key: string) => {
  key = key.toLowerCase();
  const name = dict[key] ?? key;
  return `kb_${name}`;
};
const getAllPressedKeys = (event: KeyboardEvent) => {
  const keys: string[] = [];
  if (event.altKey) keys.push("alt");
  if (event.ctrlKey) keys.push("control");
  if (event.metaKey) keys.push("meta");
  if (event.shiftKey) keys.push("shift");
  keys.push(event.code.toLowerCase().replace(/key|digit/g, ""));
  return keys.map(getKeyId);
};
export const Keyboard = () => {
  const keyboardRef = useRef<SVGSVGElement>(null);
  const isInView = useInView(keyboardRef);
  useEffect(() => {
    const keyboard = keyboardRef.current;
    if (!keyboard) return;
    const keyPress = (e: KeyboardEvent) => {
      const keyIds = getAllPressedKeys(e);
      for (const keyId of keyIds) {
        const key = keyboard.getElementById(keyId);
        if (!key) continue;
        key.classList.add("pressed");
      }
    };
    const keyUp = (e: KeyboardEvent) => {
      if (e.code.startsWith("Meta")) {
        const keys = keyboard.querySelectorAll(".pressed");
        for (const key of keys) {
          const keyName = key.id.replace("kb_", "");
          if (keyName.length > 1) continue;
          key.classList.remove("pressed");
        }
      }
      const keyIds = getAllPressedKeys(e);
      for (const keyId of keyIds) {
        const key = keyboard.getElementById(keyId);
        if (!key) continue;
        key.classList.remove("pressed");
      }
    };

    const cancelAllKeys = () => {
      keyboard.querySelectorAll(".pressed").forEach((key) => {
        key.classList.remove("pressed");
      });
    };

    window.addEventListener("keydown", keyPress);
    window.addEventListener("keyup", keyUp);
    window.addEventListener("blur", cancelAllKeys);
    return () => {
      window.removeEventListener("keydown", keyPress);
      window.removeEventListener("keyup", keyUp);
      window.removeEventListener("blur", cancelAllKeys);
    };
  }, []);

  useEffect(() => {
    if (!isInView) return;
    let index = -1;
    const combinations = [
      ["MetaLeft", "k"],
      ["MetaLeft", "ShiftLeft", "e"],
      ["MetaLeft", "r"],
      ["ShiftRight", "ArrowUp"],
    ];

    const animateNextKey = () => {
      index = (index + 1) % combinations.length;
      const keys = combinations[index];

      keys.forEach((key, index) => {
        const keyId = getKeyId(key);
        const keyEl = keyboardRef.current?.getElementById(keyId);
        if (!keyEl) return;

        setTimeout(() => {
          keyEl.classList.add("forced-pressed");
        }, index * 250);
      });

      setTimeout(() => {
        keyboardRef.current
          ?.querySelectorAll(".forced-pressed")
          .forEach((el) => {
            el.classList.remove("forced-pressed");
          });
      }, 2000);
    };

    const interval = setInterval(animateNextKey, 8000);

    return () => clearInterval(interval);
  }, [isInView]);

  return (
    <>
      <svg
        viewBox="0 0 3635 1562"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="keyboard select-none text-white/70"
        ref={keyboardRef}
      >
        <defs>
          <filter
            id="stroke"
            x="0"
            y="0"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feMorphology
              radius="6"
              operator="dilate"
              in="SourceAlpha"
              result="effect1_dropShadow_203_999"
            />
            <feOffset />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_203_999"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_203_999"
              result="shape"
            />
          </filter>
          <linearGradient
            id="kb_gradient_key"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" style={{ stopColor: "#0D0D0D" }} />
            <stop offset="100%" style={{ stopColor: "#0D0D0D80" }} />
          </linearGradient>
          <linearGradient
            id="kb_gradient_key_pressed"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" style={{ stopColor: "rgba(13, 13, 13, 0.98)" }} />
            <stop
              offset="100%"
              style={{ stopColor: "rgba(23, 23, 23, 0.9)" }}
            />
          </linearGradient>
          <linearGradient
            id="kb_paint0_linear_10_829"
            x1="3442.9"
            y1="134.096"
            x2="3442.9"
            y2="204.14"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#D8D8D8" />
            <stop offset="1" stopColor="#DCDCDC" />
          </linearGradient>
          <linearGradient
            id="kb_paint1_linear_10_829"
            x1="3478.1"
            y1="134.096"
            x2="3478.1"
            y2="204.141"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#D8D8D8" />
            <stop offset="1" stopColor="#DCDCDC" />
          </linearGradient>
          <clipPath id="kb_clip0_10_829">
            <rect
              width="64"
              height="64"
              fill="white"
              transform="translate(92 1402)"
            />
          </clipPath>
        </defs>
        <g id="kb_keyboard">
          <g id="kb_Function">
            <g id="kb_escape">
              <g id="kb_key">
                <rect className="bg" x="65" y="65" width="332" rx="21" />
              </g>
              <text id="kb_esc" fill="currentColor" fontSize="47.5">
                <tspan x="94" y="252.273">
                  esc
                </tspan>
              </text>
            </g>
            <g id="kb_f1">
              <rect className="bg" x="423" y="65" rx="21" />
              <text id="kb_F1" fill="currentColor" fontSize="32">
                <tspan x="513.325" y="246.636">
                  F1
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#134;&#171;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="502.422" y="161.062">
                  &#x1001ab;
                </tspan>
              </text>
            </g>
            <g id="kb_f2">
              <rect className="bg" x="667" y="65" rx="21" />
              <text id="kb_F2" fill="currentColor" fontSize="32">
                <tspan x="752.075" y="246.636">
                  F2
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#134;&#173;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="741.195" y="161.062">
                  &#x1001ad;
                </tspan>
              </text>
            </g>
            <g id="kb_f3">
              <rect className="bg" x="911" y="65" rx="21" />
              <text id="kb_F3" fill="currentColor" fontSize="32">
                <tspan x="995.075" y="246.636">
                  F3
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#135;&#180;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="976.102" y="161.062">
                  &#x1001f4;
                </tspan>
              </text>
            </g>
            <g id="kb_f4">
              <rect className="bg" x="1155" y="65" rx="21" />
              <text id="kb_F4" fill="currentColor" fontSize="32">
                <tspan x="1243.42" y="246.636">
                  F4
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#138;&#171;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="1234.45" y="161.062">
                  &#x1002ab;
                </tspan>
              </text>
            </g>
            <g id="kb_f5">
              <rect className="bg" x="1399" y="65" rx="21" />
              <text id="kb_F5" fill="currentColor" fontSize="32">
                <tspan x="1487.47" y="246.636">
                  F5
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#138;&#176;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="1482.18" y="161.062">
                  &#x1002b0;
                </tspan>
              </text>
            </g>
            <g id="kb_f6">
              <rect className="bg" x="1643" y="65" rx="21" />
              <text id="kb_F6" fill="currentColor" fontSize="32">
                <tspan x="1732.22" y="246.636">
                  F6
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#134;&#185;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="1724.09" y="161.062">
                  &#x1001b9;
                </tspan>
              </text>
            </g>
            <g id="kb_f7">
              <rect className="bg" x="1887" y="65" rx="21" />
              <text id="kb_F7" fill="currentColor" fontSize="32">
                <tspan x="1976.12" y="246.636">
                  F7
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#138;&#137;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="1958.44" y="161.062">
                  &#x100289;
                </tspan>
              </text>
            </g>
            <g id="kb_f8">
              <rect className="bg" x="2131" y="65" rx="21" />
              <text id="kb_F8" fill="currentColor" fontSize="32">
                <tspan x="2220.28" y="246.636">
                  F8
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#138;&#135;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="2202.48" y="161.062">
                  &#x100287;
                </tspan>
              </text>
            </g>
            <g id="kb_f9">
              <rect className="bg" x="2375" y="65" rx="21" />
              <text id="kb_F9" fill="currentColor" fontSize="32">
                <tspan x="2464.22" y="246.636">
                  F9
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#138;&#139;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="2447.44" y="161.062">
                  &#x10028b;
                </tspan>
              </text>
            </g>
            <g id="kb_f10">
              <rect className="bg" x="2619" y="65" rx="21" />
              <text id="kb_F10" fill="currentColor" fontSize="32">
                <tspan x="2703.26" y="246.636">
                  F10
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#138;&#160;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="2707.12" y="161.062">
                  &#x1002a0;
                </tspan>
              </text>
            </g>
            <g id="kb_f11">
              <rect className="bg" x="2863" y="65" rx="21" />
              <text id="kb_F11" fill="currentColor" fontSize="32">
                <tspan x="2942.02" y="246.636">
                  F11
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#138;&#164;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="2938.17" y="161.062">
                  &#x1002a4;
                </tspan>
              </text>
            </g>
            <g id="kb_f12">
              <rect className="bg" x="3107" y="65" rx="21" />
              <text id="kb_F12" fill="currentColor" fontSize="32">
                <tspan x="3188.27" y="246.636">
                  F12
                </tspan>
              </text>
              <text
                id="kb_&#244;&#128;&#138;&#168;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="3175.42" y="161.062">
                  &#x1002a8;
                </tspan>
              </text>
            </g>
            <g id="kb_power">
              <rect className="bg" x="3351" y="65" rx="21" />
              <circle id="kb_Ellipse 166" cx="3460" cy="174" r="73" />
              <path
                id="kb_Vector"
                d="M3457.79 150.366C3457.79 145.898 3451.88 144.31 3449.64 148.177L3428.36 184.927C3428 185.558 3427.9 186.311 3428.1 187.015C3428.3 187.718 3428.77 188.311 3429.41 188.662L3451.33 200.616C3454.24 202.204 3457.79 200.096 3457.79 196.779L3457.79 150.366Z"
                fill="url(#kb_paint0_linear_10_829)"
              />
              <path
                id="kb_Vector_2"
                d="M3492.64 184.927L3471.36 148.176C3469.12 144.309 3463.21 145.897 3463.21 150.365V196.779C3463.21 200.096 3466.76 202.204 3469.67 200.616L3491.59 188.66C3492.23 188.31 3492.7 187.716 3492.9 187.013C3493.1 186.31 3493 185.557 3492.63 184.925L3492.64 184.927Z"
                fill="url(#kb_paint1_linear_10_829)"
              />
            </g>
          </g>
          <g id="kb_Numbers">
            <g id="kb_intlbackslash">
              <rect className="bg" x="65" y="308" rx="21" />
              <text id="kb_&#194;&#167;" fill="currentColor" fontSize="64">
                <tspan x="156" y="489.273">
                  &#xa7;
                </tspan>
              </text>
              <text id="kb_&#194;&#177;" fill="currentColor" fontSize="64">
                <tspan x="153" y="398.273">
                  &#xb1;
                </tspan>
              </text>
            </g>
            <g id="kb_1">
              <rect className="bg" x="309" y="308" rx="21" />
              <text id="kb_1_2" fill="currentColor" fontSize="64">
                <tspan x="399" y="489.273">
                  1
                </tspan>
              </text>
              <text id="kb_!" fill="currentColor" fontSize="48">
                <tspan x="411.445" y="392.455">
                  !
                </tspan>
              </text>
            </g>
            <g id="kb_2">
              <rect className="bg" x="553" y="308" rx="21" />
              <text id="kb_2_2" fill="currentColor" fontSize="64">
                <tspan x="642" y="489.273">
                  2
                </tspan>
              </text>
              <text id="kb_@" fill="currentColor" fontSize="48">
                <tspan x="639.273" y="392.455">
                  @
                </tspan>
              </text>
            </g>
            <g id="kb_3">
              <rect className="bg" x="797" y="308" rx="21" />

              <text id="kb_3_2" fill="currentColor" fontSize="64">
                <tspan x="885" y="489.273">
                  3
                </tspan>
              </text>
              <text id="kb_#" fill="currentColor" fontSize="48">
                <tspan x="891.219" y="392.455">
                  #
                </tspan>
              </text>
            </g>
            <g id="kb_4">
              <rect className="bg" x="1041" y="308" rx="21" />
              <text id="kb_4_2" fill="currentColor" fontSize="64">
                <tspan x="1129" y="489.273">
                  4
                </tspan>
              </text>
              <text id="kb_$" fill="currentColor" fontSize="48">
                <tspan x="1135.05" y="392.455">
                  $
                </tspan>
              </text>
            </g>
            <g id="kb_5">
              <rect className="bg" x="1285" y="308" rx="21" />
              <text id="kb_5_2" fill="currentColor" fontSize="64">
                <tspan x="1374" y="489.273">
                  5
                </tspan>
              </text>
              <text id="kb_%" fill="currentColor" fontSize="48">
                <tspan x="1374.12" y="392.455">
                  %
                </tspan>
              </text>
            </g>
            <g id="kb_6">
              <rect className="bg" x="1529" y="308" rx="21" />
              <text id="kb_6_2" fill="currentColor" fontSize="64">
                <tspan x="1618" y="489.273">
                  6
                </tspan>
              </text>
              <text id="kb_^" fill="currentColor" fontSize="64">
                <tspan x="1618.16" y="397.75">
                  ^
                </tspan>
              </text>
            </g>
            <g id="kb_7">
              <rect className="bg" x="1773" y="308" rx="21" />
              <text id="kb_7_2" fill="currentColor" fontSize="64">
                <tspan x="1863" y="489.273">
                  7
                </tspan>
              </text>
              <text id="kb_&#38;" fill="currentColor" fontSize="48">
                <tspan x="1866.37" y="392.455">
                  &#38;
                </tspan>
              </text>
            </g>
            <g id="kb_8">
              <rect className="bg" x="2017" y="308" rx="21" />
              <text id="kb_8_2" fill="currentColor" fontSize="64">
                <tspan x="2104" y="489.273">
                  8
                </tspan>
              </text>
              <text id="kb_*" fill="currentColor" fontSize="64">
                <tspan x="2109.31" y="407.273">
                  *
                </tspan>
              </text>
            </g>
            <g id="kb_9">
              <rect className="bg" x="2261" y="308" rx="21" />
              <text id="kb_9_2" fill="currentColor" fontSize="64">
                <tspan x="2348" y="489.273">
                  9
                </tspan>
              </text>
              <text id="kb_(" fill="currentColor" fontSize="48">
                <tspan x="2361.43" y="392.455">
                  (
                </tspan>
              </text>
            </g>
            <g id="kb_0">
              <g id="kb_Group 31">
                <rect className="bg" x="2505" y="308" rx="21" />
              </g>
              <text id="kb_0_2" fill="currentColor" fontSize="64">
                <tspan x="2593" y="489.273">
                  0
                </tspan>
              </text>
              <text id="kb_)" fill="currentColor" fontSize="48">
                <tspan x="2605.43" y="392.455">
                  )
                </tspan>
              </text>
            </g>
            <g id="kb_minus">
              <rect className="bg" x="2749" y="308" rx="21" />
              <text id="kb_-" fill="currentColor" fontSize="64">
                <tspan x="2843.19" y="400.273">
                  -
                </tspan>
              </text>
              <text id="kb__" fill="currentColor" fontSize="64">
                <tspan x="2843" y="473.273">
                  _
                </tspan>
              </text>
            </g>
            <g id="kb_equal">
              <rect className="bg" x="2993" y="308" rx="21" />
              <text id="kb_+" fill="currentColor" fontSize="64">
                <tspan x="3081.19" y="400.273">
                  +
                </tspan>
              </text>
              <text id="kb_=" fill="currentColor" fontSize="64">
                <tspan x="3081" y="489.273">
                  =
                </tspan>
              </text>
            </g>
            <g id="kb_backspace">
              <rect className="bg" x="3237" y="308" width="333" rx="21" />
              <text
                id="kb_&#226;&#140;&#171;"
                fill="currentColor"
                fontSize="56"
              >
                <tspan x="3461" y="491.364">
                  &#x232b;
                </tspan>
              </text>
            </g>
          </g>
          <g id="kb_Q -&#62; P">
            <g id="kb_tab">
              <rect className="bg" x="65" y="551" width="309" rx="21" />
              <text
                id="kb_&#244;&#128;&#133;&#130;"
                fill="currentColor"
                fontSize="68"
                fontWeight="510"
              >
                <tspan x="92.2266" y="736.062">
                  ⇥
                </tspan>
              </text>
            </g>
            <g id="kb_q">
              <rect className="bg" x="400" y="551" rx="21" />
              <text id="kb_Q" fill="currentColor" fontSize="64">
                <tspan x="485" y="683.273">
                  Q
                </tspan>
              </text>
            </g>
            <g id="kb_w">
              <rect className="bg" x="644" y="551" rx="21" />
              <text id="kb_W" fill="currentColor" fontSize="64">
                <tspan x="723" y="683.273">
                  W
                </tspan>
              </text>
            </g>
            <g id="kb_e">
              <rect className="bg" x="888" y="551" rx="21" />
              <text id="kb_E" fill="currentColor" fontSize="64">
                <tspan x="979" y="683.273">
                  E
                </tspan>
              </text>
            </g>
            <g id="kb_r">
              <rect className="bg" x="1132" y="551" rx="21" />
              <text id="kb_R" fill="currentColor" fontSize="64">
                <tspan x="1221" y="683.273">
                  R
                </tspan>
              </text>
            </g>
            <g id="kb_t">
              <rect className="bg" x="1376" y="551" rx="21" />
              <text id="kb_T" fill="currentColor" fontSize="64">
                <tspan x="1465" y="683.273">
                  T
                </tspan>
              </text>
            </g>
            <g id="kb_y">
              <rect className="bg" x="1620" y="551" rx="21" />
              <text id="kb_Y" fill="currentColor" fontSize="64">
                <tspan x="1708" y="683.273">
                  Y
                </tspan>
              </text>
            </g>
            <g id="kb_u">
              <rect className="bg" x="1864" y="551" rx="21" />
              <text id="kb_U" fill="currentColor" fontSize="64">
                <tspan x="1950" y="683.273">
                  U
                </tspan>
              </text>
            </g>
            <g id="kb_i">
              <rect className="bg" x="2108" y="551" rx="21" />
              <text id="kb_I" fill="currentColor" fontSize="64">
                <tspan x="2209" y="683.273">
                  I
                </tspan>
              </text>
            </g>
            <g id="kb_o">
              <rect className="bg" x="2352" y="551" rx="21" />
              <text id="kb_O" fill="currentColor" fontSize="64">
                <tspan x="2437" y="683.273">
                  O
                </tspan>
              </text>
            </g>
            <g id="kb_p">
              <rect className="bg" x="2596" y="551" rx="21" />
              <text id="kb_P" fill="currentColor" fontSize="64">
                <tspan x="2685" y="682.273">
                  P
                </tspan>
              </text>
            </g>
            <g id="kb_bracketleft">
              <rect className="bg" x="2840" y="551" rx="21" />
              <text id="kb_{" fill="currentColor" fontSize="64">
                <tspan x="2937" y="643.273">
                  {`{`}
                </tspan>
              </text>
              <text id="kb_[" fill="currentColor" fontSize="64">
                <tspan x="2937" y="732.273">
                  [
                </tspan>
              </text>
            </g>
            <g id="kb_bracketright">
              <rect className="bg" x="3084" y="551" rx="21" />
              <text id="kb_}" fill="currentColor" fontSize="64">
                <tspan x="3177" y="643.273">
                  &#125;
                </tspan>
              </text>
              <text id="kb_]" fill="currentColor" fontSize="64">
                <tspan x="3177" y="732.273">
                  ]
                </tspan>
              </text>
            </g>
            <g id="kb_enter">
              <path
                className="bg"
                d="M3349 551H3548.5C3560.1 551 3569.5 560.402 3569.5 572V989C3569.5 1000.6 3560.1 1010 3548.5 1010H3425C3413.4 1010 3404 1000.6 3404 989V789.5C3404 776.797 3393.7 766.5 3381 766.5H3349C3337.4 766.5 3328 757.098 3328 745.5V572C3328 560.402 3337.4 551 3349 551Z"
              />
              <text
                id="kb_&#244;&#128;&#133;&#135;"
                fill="currentColor"
                fontSize="48"
                fontWeight="510"
              >
                <tspan x="3463.12" y="749.062">
                  &#11152;
                </tspan>
              </text>
            </g>
          </g>
          <g id="kb_A -&#62; L">
            <g id="kb_capslock">
              <rect className="bg" x="65" y="794" width="385" rx="21" />
              <text
                id="kb_&#226;&#135;&#170;"
                fill="currentColor"
                fontSize="56"
              >
                <tspan x="93" y="970.364">
                  &#x21ea;
                </tspan>
              </text>
              <circle
                id="kb_Ellipse 167"
                cx="108.5"
                cy="841.5"
                r="8.5"
                fill="#94FFB2"
              />
            </g>
            <g id="kb_a">
              <rect className="bg" x="476" y="794" rx="21" />
              <text id="kb_A" fill="currentColor" fontSize="64">
                <tspan x="562" y="926.273">
                  A
                </tspan>
              </text>
            </g>
            <g id="kb_s">
              <rect className="bg" x="720" y="794" rx="21" />
              <text id="kb_S" fill="currentColor" fontSize="64">
                <tspan x="807" y="928.273">
                  S
                </tspan>
              </text>
            </g>
            <g id="kb_d">
              <rect className="bg" x="964" y="794" rx="21" />
              <text id="kb_D" fill="currentColor" fontSize="64">
                <tspan x="1049" y="928.273">
                  D
                </tspan>
              </text>
            </g>
            <g id="kb_f">
              <rect className="bg" x="1208" y="794" rx="21" />
              <text id="kb_F" fill="currentColor" fontSize="64">
                <tspan x="1297" y="928.273">
                  F
                </tspan>
              </text>
            </g>
            <g id="kb_g">
              <rect className="bg" x="1452" y="794" rx="21" />
              <text id="kb_G" fill="currentColor" fontSize="64">
                <tspan x="1536" y="928.273">
                  G
                </tspan>
              </text>
            </g>
            <g id="kb_h">
              <rect className="bg" x="1696" y="794" rx="21" />
              <text id="kb_H" fill="currentColor" fontSize="64">
                <tspan x="1780" y="928.273">
                  H
                </tspan>
              </text>
            </g>
            <g id="kb_j">
              <rect className="bg" x="1940" y="794" rx="21" />
              <text id="kb_J" fill="currentColor" fontSize="64">
                <tspan x="2030" y="928.273">
                  J
                </tspan>
              </text>
            </g>
            <g id="kb_k">
              <rect className="bg" x="2184" y="794" rx="21" />
              <text id="kb_K" fill="currentColor" fontSize="64">
                <tspan x="2271" y="928.273">
                  K
                </tspan>
              </text>
            </g>
            <g id="kb_l">
              <rect className="bg" x="2428" y="794" rx="21" />
              <text id="kb_L" fill="currentColor" fontSize="64">
                <tspan x="2518" y="928.273">
                  L
                </tspan>
              </text>
            </g>
            <g id="kb_semicolon">
              <rect className="bg" x="2672" y="794" rx="21" />
              <text id="kb_:" fill="currentColor" fontSize="64">
                <tspan x="2772.44" y="888.273">
                  :
                </tspan>
              </text>
              <text id="kb_;" fill="currentColor" fontSize="64">
                <tspan x="2772.28" y="958.273">
                  ;
                </tspan>
              </text>
            </g>
            <g id="kb_quote">
              <rect className="bg" x="2916" y="794" rx="21" />
              <text
                id="kb_&#226;&#128;&#156;"
                fill="currentColor"
                fontSize="64"
              >
                <tspan x="3012.38" y="883.273">
                  &#x201c;
                </tspan>
              </text>
              <text
                id="kb_&#226;&#128;&#152;"
                fill="currentColor"
                fontSize="64"
              >
                <tspan x="3018.16" y="972.273">
                  &#x2018;
                </tspan>
              </text>
            </g>
            <g id="kb_backslash">
              <rect className="bg" x="3160" y="794" rx="21" />
              <text id="kb_|" fill="currentColor" fontSize="64">
                <tspan x="3258" y="888.773">
                  |
                </tspan>
              </text>
              <text id="kb_backwardslash" fill="currentColor" fontSize="64">
                <tspan x="3257" y="977.773">
                  \
                </tspan>
              </text>
            </g>
          </g>
          <g id="kb_Z -&#62; M">
            <g id="kb_shiftleft">
              <rect className="bg" x="65" y="1037" width="272" rx="21" />
              <text
                id="kb_&#226;&#135;&#167;"
                fill="currentColor"
                fontSize="56"
              >
                <tspan x="93" y="1222.36">
                  &#x21e7;
                </tspan>
              </text>
            </g>
            <g id="kb_tilde_2">
              <rect className="bg" x="363" y="1037" rx="21" />
              <text id="kb_`" fill="currentColor" fontSize="64">
                <tspan x="456" y="1231.27">
                  `
                </tspan>
              </text>
              <text id="kb_~" fill="currentColor" fontSize="64">
                <tspan x="451" y="1127.27">
                  ~
                </tspan>
              </text>
            </g>
            <g id="kb_z">
              <rect className="bg" x="607" y="1037" rx="21" />
              <text id="kb_Z" fill="currentColor" fontSize="64">
                <tspan x="696" y="1169.27">
                  Z
                </tspan>
              </text>
            </g>
            <g id="kb_x">
              <rect className="bg" x="851" y="1037" rx="21" />
              <text id="kb_X" fill="currentColor" fontSize="64">
                <tspan x="939" y="1169.27">
                  X
                </tspan>
              </text>
            </g>
            <g id="kb_c">
              <rect className="bg" x="1095" y="1037" rx="21" />
              <text id="kb_C" fill="currentColor" fontSize="64">
                <tspan x="1180" y="1169.27">
                  C
                </tspan>
              </text>
            </g>
            <g id="kb_v">
              <rect className="bg" x="1339" y="1037" rx="21" />
              <text id="kb_V" fill="currentColor" fontSize="64">
                <tspan x="1426" y="1169.27">
                  V
                </tspan>
              </text>
            </g>
            <g id="kb_b">
              <rect className="bg" x="1583" y="1037" rx="21" />
              <text id="kb_B" fill="currentColor" fontSize="64">
                <tspan x="1671" y="1169.27">
                  B
                </tspan>
              </text>
            </g>
            <g id="kb_n">
              <rect className="bg" x="1827" y="1037" rx="21" />
              <text id="kb_N" fill="currentColor" fontSize="64">
                <tspan x="1912" y="1169.27">
                  N
                </tspan>
              </text>
            </g>
            <g id="kb_m">
              <rect className="bg" x="2071" y="1037" rx="21" />
              <text id="kb_M" fill="currentColor" fontSize="64">
                <tspan x="2151" y="1169.27">
                  M
                </tspan>
              </text>
            </g>
            <g id="kb_comma">
              <rect className="bg" x="2315" y="1037" rx="21" />
              <text id="kb_&#60;" fill="currentColor" fontSize="64">
                <tspan x="2403" y="1129.27">
                  &#60;
                </tspan>
              </text>
              <text id="kb_," fill="currentColor" fontSize="64">
                <tspan x="2415" y="1199.27">
                  ,
                </tspan>
              </text>
            </g>
            <g id="kb_period">
              <rect className="bg" x="2559" y="1037" rx="21" />
              <text id="kb_&#62;" fill="currentColor" fontSize="64">
                <tspan x="2647" y="1129.27">
                  &#62;
                </tspan>
              </text>
              <text id="kb_." fill="currentColor" fontSize="64">
                <tspan x="2659" y="1199.27">
                  .
                </tspan>
              </text>
            </g>
            <g id="kb_slash">
              <rect className="bg" x="2803" y="1037" rx="21" />
              <text id="kb_?" fill="currentColor" fontSize="64">
                <tspan x="2897" y="1129.27">
                  ?
                </tspan>
              </text>
              <text id="kb_/" fill="currentColor" fontSize="64">
                <tspan x="2897" y="1218.27">
                  /
                </tspan>
              </text>
            </g>
            <g id="kb_shiftright">
              <rect className="bg" x="3047" y="1037" width="521" rx="21" />
              <text
                id="kb_&#226;&#135;&#167;_2"
                fill="currentColor"
                fontSize="56"
              >
                <tspan x="3481" y="1222.36">
                  &#x21e7;
                </tspan>
              </text>
            </g>
          </g>
          <g id="kb_Modifiers">
            <g id="kb_fn">
              <rect className="bg" x="65" y="1280" rx="21" />
              <text id="kb_fn_2" fill="currentColor" fontSize="47.5">
                <tspan x="201" y="1347.27">
                  fn
                </tspan>
              </text>
              <g id="kb_Logo" clipPath="url(#clip0_10_829)">
                <path
                  id="kb_Vector_3"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M108 1443.32V1450L91.9854 1433.99L95.3411 1430.66L108 1443.32ZM114.679 1450H108L124.014 1466.01L127.356 1462.67L114.679 1450ZM152.662 1437.34L156 1434L124 1402L120.662 1405.34L133.307 1418H125.663L116.837 1409.18L113.496 1412.52L118.992 1418.01H115.166V1442.85H140V1439.03L145.496 1444.52L148.833 1441.18L140 1432.33V1424.69L152.662 1437.34ZM109.671 1416.33L106.333 1419.67L109.914 1423.25L113.252 1419.91L109.671 1416.33ZM138.089 1444.75L134.762 1448.09L138.344 1451.67L141.685 1448.33L138.089 1444.75ZM102.504 1423.5L99.1664 1426.83L108 1435.67V1428.99L102.504 1423.5ZM129.008 1450H122.329L131.166 1458.83L134.504 1455.5L129.008 1450Z"
                  fill="currentColor"
                />
              </g>
            </g>
            <g id="kb_controlleft">
              <rect className="bg" x="309" y="1280" rx="21" />
              <text id="kb_control" fill="currentColor" fontSize="47.5">
                <tspan x="332" y="1464.27">
                  control
                </tspan>
              </text>
              <path
                id="kb_Vector 368"
                d="M495 1334.5L477.5 1317L460 1334.5"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <g id="kb_altleft">
              <rect className="bg" x="553" y="1280" rx="21" />
              <text id="kb_option_2" fill="currentColor" fontSize="47.5">
                <tspan x="589" y="1464.27">
                  option
                </tspan>
              </text>
              <text
                id="kb_&#226;&#140;&#165;"
                fill="currentColor"
                fontSize="47.5"
              >
                <tspan x="687" y="1345.27">
                  &#x2325;
                </tspan>
              </text>
            </g>
            <g id="kb_metaleft">
              <rect className="bg" x="797" y="1280" width="275" rx="21" />
              <path
                id="kb_Vector_4"
                d="M991.818 1330.64H997.273V1339.36H991.818C989.876 1339.36 987.978 1339.94 986.363 1341.02C984.749 1342.1 983.49 1343.63 982.747 1345.42C982.004 1347.22 981.81 1349.19 982.189 1351.1C982.567 1353 983.503 1354.75 984.876 1356.12C986.249 1357.5 987.998 1358.43 989.903 1358.81C991.807 1359.19 993.781 1359 995.575 1358.25C997.369 1357.51 998.903 1356.25 999.982 1354.64C1001.06 1353.02 1001.64 1351.12 1001.64 1349.18V1343.73H1010.36V1349.18C1010.36 1351.12 1010.94 1353.02 1012.02 1354.64C1013.1 1356.25 1014.63 1357.51 1016.42 1358.25C1018.22 1359 1020.19 1359.19 1022.1 1358.81C1024 1358.43 1025.75 1357.5 1027.12 1356.12C1028.5 1354.75 1029.43 1353 1029.81 1351.1C1030.19 1349.19 1030 1347.22 1029.25 1345.42C1028.51 1343.63 1027.25 1342.1 1025.64 1341.02C1024.02 1339.94 1022.12 1339.36 1020.18 1339.36H1014.73V1330.64H1020.18C1022.12 1330.64 1024.02 1330.06 1025.64 1328.98C1027.25 1327.9 1028.51 1326.37 1029.25 1324.58C1030 1322.78 1030.19 1320.81 1029.81 1318.9C1029.43 1317 1028.5 1315.25 1027.12 1313.88C1025.75 1312.5 1024 1311.57 1022.1 1311.19C1020.19 1310.81 1018.22 1311 1016.42 1311.75C1014.63 1312.49 1013.1 1313.75 1012.02 1315.36C1010.94 1316.98 1010.36 1318.88 1010.36 1320.82V1326.27H1001.64V1320.82C1001.64 1318.88 1001.06 1316.98 999.982 1315.36C998.903 1313.75 997.369 1312.49 995.575 1311.75C993.781 1311 991.807 1310.81 989.903 1311.19C987.998 1311.57 986.249 1312.5 984.876 1313.88C983.503 1315.25 982.567 1317 982.189 1318.9C981.81 1320.81 982.004 1322.78 982.747 1324.58C983.49 1326.37 984.749 1327.9 986.363 1328.98C987.978 1330.06 989.876 1330.64 991.818 1330.64ZM997.273 1349.18C997.273 1350.26 996.953 1351.32 996.353 1352.21C995.754 1353.11 994.902 1353.81 993.906 1354.22C992.909 1354.63 991.812 1354.74 990.754 1354.53C989.696 1354.32 988.724 1353.8 987.961 1353.04C987.198 1352.28 986.679 1351.3 986.468 1350.25C986.258 1349.19 986.366 1348.09 986.779 1347.09C987.192 1346.1 987.891 1345.25 988.788 1344.65C989.685 1344.05 990.739 1343.73 991.818 1343.73H997.273V1349.18ZM1025.64 1349.18C1025.64 1350.63 1025.06 1352.02 1024.04 1353.04C1023.02 1354.06 1021.63 1354.64 1020.18 1354.64C1018.74 1354.64 1017.35 1354.06 1016.32 1353.04C1015.3 1352.02 1014.73 1350.63 1014.73 1349.18V1343.73H1020.18C1021.63 1343.73 1023.02 1344.3 1024.04 1345.32C1025.06 1346.35 1025.64 1347.74 1025.64 1349.18ZM1014.73 1320.82C1014.73 1319.74 1015.05 1318.68 1015.65 1317.79C1016.25 1316.89 1017.1 1316.19 1018.09 1315.78C1019.09 1315.37 1020.19 1315.26 1021.25 1315.47C1022.3 1315.68 1023.28 1316.2 1024.04 1316.96C1024.8 1317.72 1025.32 1318.7 1025.53 1319.75C1025.74 1320.81 1025.63 1321.91 1025.22 1322.91C1024.81 1323.9 1024.11 1324.75 1023.21 1325.35C1022.32 1325.95 1021.26 1326.27 1020.18 1326.27H1014.73V1320.82ZM1010.36 1339.36H1001.64V1330.64H1010.36V1339.36ZM991.818 1315.36C993.265 1315.36 994.652 1315.94 995.675 1316.96C996.698 1317.98 997.273 1319.37 997.273 1320.82V1326.27H991.818C990.372 1326.27 988.984 1325.7 987.961 1324.68C986.938 1323.65 986.364 1322.26 986.364 1320.82C986.364 1319.37 986.938 1317.98 987.961 1316.96C988.984 1315.94 990.372 1315.36 991.818 1315.36Z"
                fill="currentColor"
              />
              <text id="kb_metaright" fill="currentColor" fontSize="47.5">
                <tspan x="820" y="1464.27">
                  command
                </tspan>
              </text>
            </g>
            <g id="kb_space">
              <rect className="bg" x="1098" y="1280" width="1195" rx="21" />
            </g>
            <g id="kb_command_3">
              <rect className="bg" x="2319" y="1280" width="275" rx="21" />
              <path
                id="kb_Vector_5"
                d="M2513.82 1330.64H2519.27V1339.36H2513.82C2511.88 1339.36 2509.98 1339.94 2508.36 1341.02C2506.75 1342.1 2505.49 1343.63 2504.75 1345.42C2504 1347.22 2503.81 1349.19 2504.19 1351.1C2504.57 1353 2505.5 1354.75 2506.88 1356.12C2508.25 1357.5 2510 1358.43 2511.9 1358.81C2513.81 1359.19 2515.78 1359 2517.58 1358.25C2519.37 1357.51 2520.9 1356.25 2521.98 1354.64C2523.06 1353.02 2523.64 1351.12 2523.64 1349.18V1343.73H2532.36V1349.18C2532.36 1351.12 2532.94 1353.02 2534.02 1354.64C2535.1 1356.25 2536.63 1357.51 2538.42 1358.25C2540.22 1359 2542.19 1359.19 2544.1 1358.81C2546 1358.43 2547.75 1357.5 2549.12 1356.12C2550.5 1354.75 2551.43 1353 2551.81 1351.1C2552.19 1349.19 2552 1347.22 2551.25 1345.42C2550.51 1343.63 2549.25 1342.1 2547.64 1341.02C2546.02 1339.94 2544.12 1339.36 2542.18 1339.36H2536.73V1330.64H2542.18C2544.12 1330.64 2546.02 1330.06 2547.64 1328.98C2549.25 1327.9 2550.51 1326.37 2551.25 1324.58C2552 1322.78 2552.19 1320.81 2551.81 1318.9C2551.43 1317 2550.5 1315.25 2549.12 1313.88C2547.75 1312.5 2546 1311.57 2544.1 1311.19C2542.19 1310.81 2540.22 1311 2538.42 1311.75C2536.63 1312.49 2535.1 1313.75 2534.02 1315.36C2532.94 1316.98 2532.36 1318.88 2532.36 1320.82V1326.27H2523.64V1320.82C2523.64 1318.88 2523.06 1316.98 2521.98 1315.36C2520.9 1313.75 2519.37 1312.49 2517.58 1311.75C2515.78 1311 2513.81 1310.81 2511.9 1311.19C2510 1311.57 2508.25 1312.5 2506.88 1313.88C2505.5 1315.25 2504.57 1317 2504.19 1318.9C2503.81 1320.81 2504 1322.78 2504.75 1324.58C2505.49 1326.37 2506.75 1327.9 2508.36 1328.98C2509.98 1330.06 2511.88 1330.64 2513.82 1330.64ZM2519.27 1349.18C2519.27 1350.26 2518.95 1351.32 2518.35 1352.21C2517.75 1353.11 2516.9 1353.81 2515.91 1354.22C2514.91 1354.63 2513.81 1354.74 2512.75 1354.53C2511.7 1354.32 2510.72 1353.8 2509.96 1353.04C2509.2 1352.28 2508.68 1351.3 2508.47 1350.25C2508.26 1349.19 2508.37 1348.09 2508.78 1347.09C2509.19 1346.1 2509.89 1345.25 2510.79 1344.65C2511.68 1344.05 2512.74 1343.73 2513.82 1343.73H2519.27V1349.18ZM2547.64 1349.18C2547.64 1350.63 2547.06 1352.02 2546.04 1353.04C2545.02 1354.06 2543.63 1354.64 2542.18 1354.64C2540.74 1354.64 2539.35 1354.06 2538.32 1353.04C2537.3 1352.02 2536.73 1350.63 2536.73 1349.18V1343.73H2542.18C2543.63 1343.73 2545.02 1344.3 2546.04 1345.32C2547.06 1346.35 2547.64 1347.74 2547.64 1349.18ZM2536.73 1320.82C2536.73 1319.74 2537.05 1318.68 2537.65 1317.79C2538.25 1316.89 2539.1 1316.19 2540.09 1315.78C2541.09 1315.37 2542.19 1315.26 2543.25 1315.47C2544.3 1315.68 2545.28 1316.2 2546.04 1316.96C2546.8 1317.72 2547.32 1318.7 2547.53 1319.75C2547.74 1320.81 2547.63 1321.91 2547.22 1322.91C2546.81 1323.9 2546.11 1324.75 2545.21 1325.35C2544.32 1325.95 2543.26 1326.27 2542.18 1326.27H2536.73V1320.82ZM2532.36 1339.36H2523.64V1330.64H2532.36V1339.36ZM2513.82 1315.36C2515.26 1315.36 2516.65 1315.94 2517.68 1316.96C2518.7 1317.98 2519.27 1319.37 2519.27 1320.82V1326.27H2513.82C2512.37 1326.27 2510.98 1325.7 2509.96 1324.68C2508.94 1323.65 2508.36 1322.26 2508.36 1320.82C2508.36 1319.37 2508.94 1317.98 2509.96 1316.96C2510.98 1315.94 2512.37 1315.36 2513.82 1315.36Z"
                fill="currentColor"
              />
              <text id="kb_command_4" fill="currentColor" fontSize="47.5">
                <tspan x="2342" y="1464.27">
                  command
                </tspan>
              </text>
            </g>
            <g id="kb_altright">
              <rect className="bg" x="2620" y="1280" rx="21" />
              <text
                id="kb_&#226;&#140;&#165;_2"
                fill="currentColor"
                fontSize="47.5"
              >
                <tspan x="2753" y="1345.27">
                  &#x2325;
                </tspan>
              </text>
              <text id="kb_option_4" fill="currentColor" fontSize="47.5">
                <tspan x="2655" y="1464.27">
                  option
                </tspan>
              </text>
            </g>
            <g id="kb_arrowleft">
              <rect className="bg" x="2864" y="1397" height="100" rx="21" />
              <text
                id="kb_&#226;&#151;&#128;&#239;&#184;&#142;"
                fill="currentColor"
                fontSize="36"
              >
                <tspan x="2955.05" y="1462.09">
                  &#x25c0;&#xfe0e;
                </tspan>
              </text>
            </g>
            <g id="kb_Group 7">
              <g id="kb_arrowdown">
                <rect className="bg" x="3108" y="1397" height="100" rx="21" />
                <text
                  id="kb_&#226;&#150;&#188;&#239;&#184;&#142;"
                  fill="currentColor"
                  fontSize="36"
                >
                  <tspan x="3199.38" y="1462.09">
                    &#x25bc;&#xfe0e;
                  </tspan>
                </text>
              </g>
              <g id="kb_arrowup">
                <rect className="bg" x="3108" y="1280" height="100" rx="21" />
                <text
                  id="kb_&#226;&#150;&#178;"
                  fill="currentColor"
                  fontSize="36"
                >
                  <tspan x="3200.11" y="1343.09">
                    &#x25b2;
                  </tspan>
                </text>
              </g>
            </g>
            <g id="kb_arrowright">
              <rect className="bg" x="3352" y="1397" height="100" rx="21" />
              <text
                id="kb_&#226;&#150;&#182;&#239;&#184;&#142;"
                fill="currentColor"
                fontSize="36"
              >
                <tspan x="3443.05" y="1462.09">
                  &#x25b6;&#xfe0e;
                </tspan>
              </text>
            </g>
          </g>
        </g>
      </svg>
    </>
  );
};
